import { useEffect, useState } from 'react';
import { createWorkspaceEntityRepository } from '@workspace/domain/workspace';
import { ObjectRepository, TextRepository } from '@framework/repository';
import { RTDBPath } from 'balus-common';
import { RootFolderTreeRepository } from '@workspace/view-model-folder/infrastructure/FolderTree';
import { Folder } from '@workspace/view-model-folder/domain/Folder';
import { ViewModelEntity } from '@view-model/domain/view-model';
import type { FolderId, ViewModelId } from 'balus-common';
import { useSetAtom } from 'jotai';
import { setInitialKataSearchRecordsAtom } from './kataSearchResultState';
import type { SidebarSearchRecords } from '../SidebarSearchRecords';

export const useSidebarSearchKataRecords = (isSearchPanelVisible: boolean): { firstKataLoaded: boolean } => {
  const [firstKataLoaded, setFirstKataLoaded] = useState(false);
  const setInitialKataSearchRecords = useSetAtom(setInitialKataSearchRecordsAtom);

  useEffect(() => {
    if (!isSearchPanelVisible || firstKataLoaded) return;

    const fetchFirstLoadKataRecords = async (): Promise<SidebarSearchRecords | null> => {
      // KATAワークスペースの取得
      const kataWorkspaceIdRepo = new TextRepository(RTDBPath.ApplicationConfig.kataWorkspaceIdPath());
      const kataWorkspaceId = await kataWorkspaceIdRepo.get();

      if (!kataWorkspaceId) return null;

      const kataWorkspaceRepo = createWorkspaceEntityRepository(kataWorkspaceId);
      const kataWorkspace = await kataWorkspaceRepo.get();

      if (!kataWorkspace) return null;

      // KATAワークスペースのフォルダツリーの取得
      const folderTreeRepo = new RootFolderTreeRepository(kataWorkspace.id);
      const folderTree = await folderTreeRepo.load();

      if (!folderTree) return null;

      // KATAフォルダの取得
      const folderIds = folderTree.getAllFolderIds();
      const folders: Record<FolderId, Folder> = Object.fromEntries(
        await Promise.all(
          folderIds.map(async (folderId) => {
            const repo = new ObjectRepository(Folder, RTDBPath.Workspace.folderPath(folderId));
            return [folderId, await repo.get()];
          })
        )
      );

      // KATAビューモデルの取得
      const viewModelIds = folderTree.getAllViewModelIds();
      const viewModels: Record<ViewModelId, ViewModelEntity> = Object.fromEntries(
        await Promise.all(
          viewModelIds.map(async (viewModelId) => {
            const repo = new ObjectRepository(ViewModelEntity, RTDBPath.ViewModel.viewModelPath(viewModelId));
            return [viewModelId, await repo.get()];
          })
        )
      );

      return {
        workspaces: { [kataWorkspace.id]: kataWorkspace },
        folderTrees: { [kataWorkspace.id]: folderTree },
        folders,
        viewModels,
      };
    };

    fetchFirstLoadKataRecords()
      .then((records) => {
        if (records) {
          setInitialKataSearchRecords(records);
        }
      })
      .finally(() => {
        setFirstKataLoaded(true);
      });
  }, [isSearchPanelVisible, firstKataLoaded, setInitialKataSearchRecords]);

  return { firstKataLoaded };
};
